import React, { useState, useEffect, useMemo } from 'react';
import { motion, MotionConfig } from 'framer-motion';
import {
  Sparkles,
  Plus,
} from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { balloons } from "balloons-js";
import { GREETING_MESSAGES } from '../constants/greetings';

// Widgets Nuevos
import FeriadoBancarioCard from './Home/FeriadoBancarioCard';
import VentaEspecialBanner from './Home/VentaEspecialBanner';
import PremiosAnsaBanner from './Home/PremiosAnsaBanner';
import EstacionamientoCa1Banner from './Home/EstacionamientoCa1Banner';
import FeedWidget from './Home/FeedWidget';
import CreatePostModal from './Home/CreatePostModal';
import VentaEspecialWidget from './Home/VentaEspecialWidget';
import TaxiPendienteWidget from './Home/TaxiPendienteWidget';
import TaxiRecepcionWidget from './Home/TaxiRecepcionWidget';
import ViaticosPendientesWidget from './Home/ViaticosPendientesWidget';
import CalendarioCierreWidget from './Home/CalendarioCierreWidget';
import SalasReservaWidget from './Home/SalasReservaWidget';
import CumpleanosWidget from './Home/CumpleanosWidget';
import MiDiaWidget from './Home/MiDiaWidget';
import AniversarioWidget from './Home/AniversarioWidget';
import TicketsPendientesWidget from './Home/TicketsPendientesWidget';
import MisTareasWidget from './Home/MisTareasWidget';
import EncuestasPendientesWidget from './Home/EncuestasPendientesWidget';
import PermisosPendientesWidget from './Home/PermisosPendientesWidget';
import VacacionesPendientesWidget from './Home/VacacionesPendientesWidget';
import ConstanciasPendientesWidget from './Home/ConstanciasPendientesWidget';
import Aparecer from './Home/Aparecer';

// Secuencia de entrada de Inicio: un solo momento, de arriba hacia abajo. El
// saludo palabra por palabra, la fecha, los accesos y luego las columnas; los
// widgets se abren en orden a medida que llegan sus datos.
const ENTRADA = { type: 'spring', stiffness: 220, damping: 26 };
const subir = (retardo) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { ...ENTRADA, delay: retardo },
});


const HomeView = () => {
  const { user } = useAuth();
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estado para correos de Gestión Humana (anuncios)
  const [anuncios, setAnuncios] = useState([]);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [feedRefreshTrigger, setFeedRefreshTrigger] = useState(0);
  const [feriadoCard, setFeriadoCard] = useState(null);
  const [ventaBanner, setVentaBanner] = useState(null);
  // Momento en que se abrio Inicio: ancla la secuencia de entrada.
  const [abiertoEn] = useState(() => Date.now());

  const today = new Date();
  const formattedDate = today.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });


  const getGreeting = () => {
    const hour = today.getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const userHasBirthdayToday = useMemo(() => {
    if (!user?.empleado_id || !birthdays.length) return false;
    const currentDay = today.getDate();
    return birthdays.some(person => {
      const day = parseInt(person.date.split(' ')[0]);
      return day === currentDay && person.id === user.empleado_id;
    });
  }, [birthdays, user, today]);

  const randomMessage = useMemo(() => {
    return GREETING_MESSAGES[Math.floor(Math.random() * GREETING_MESSAGES.length)];
  }, []);

  useEffect(() => {
    const fetchBirthdays = async () => {
      try {
        const response = await axiosInstance.get('/api/empleados/cumpleaneros');
        setBirthdays(response.data);
      } catch (error) {
        console.error("Error fetching birthdays:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBirthdays();
  }, []);

  // Card de feriado bancario (aviso automatico, se autolimpia por fecha)
  useEffect(() => {
    const fetchFeriado = async () => {
      try {
        const response = await axiosInstance.get('/api/feriados/card-activa');
        setFeriadoCard(response.data?.card || null);
      } catch (error) {
        console.error('Error fetching feriado card:', error);
      }
    };
    fetchFeriado();
  }, []);

  // Banner de venta especial de empleados (solo cuando hay una venta activa)
  useEffect(() => {
    const fetchVentaActiva = async () => {
      try {
        const response = await axiosInstance.get('/api/ventas-especiales/activa');
        const venta = response.data;
        // Ocultar el banner si la venta no está publicada o si ya venció la fecha de pedido.
        const vencida = venta?.fechaLimitePedido && new Date(venta.fechaLimitePedido).getTime() <= Date.now();
        setVentaBanner(venta?.activa === false || vencida ? null : venta);
      } catch (error) {
        console.error('Error fetching venta activa:', error);
      }
    };
    fetchVentaActiva();
  }, []);

  const fetchAnuncios = async () => {
    try {
      const response = await axiosInstance.get('/api/anuncios');
      if (response.data.success) {
        setAnuncios(response.data.emails);
      }
    } catch (error) {
      console.error('Error fetching anuncios:', error);
    }
  };

  // Exponer refresh para que FeedWidget pueda refrescar tras pin
  useEffect(() => {
    window.__refreshAnuncios = fetchAnuncios;
    return () => { delete window.__refreshAnuncios; };
  }, []);

  useEffect(() => {
    fetchAnuncios();
    const interval = setInterval(fetchAnuncios, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (userHasBirthdayToday) {
      try {
        balloons();
      } catch (error) {
        console.error("Error launching balloons:", error);
      }
    }
  }, [userHasBirthdayToday]);


  const getDisplayName = () => {
    if (user?.nombre_empleado) {
      const parts = user.nombre_empleado.trim().split(/\s+/);
      return parts[0];
    }
    return user?.username?.split(' ')[0] || 'Compañero';
  };

  // El saludo entra palabra por palabra; el nombre, al final y en azul.
  const palabrasSaludo = `${getGreeting()},`.split(' ');

  return (
    <MotionConfig reducedMotion="user">
    <div className="max-w-[1600px] mx-auto pb-8">

      {/* Header */}
      <header className="mb-8 pt-4 relative">
        <h1 className="text-4xl md:text-5xl font-light text-gray-900 tracking-tight mb-3">
          {palabrasSaludo.map((palabra, i) => (
            <motion.span key={i} className="inline-block mr-[0.25em]" {...subir(i * 0.07)}>
              {palabra}
            </motion.span>
          ))}
          <motion.span className="inline-block font-semibold text-sura-blue" {...subir(palabrasSaludo.length * 0.07)}>
            {getDisplayName()}
          </motion.span>
          {userHasBirthdayToday && <span className="text-pink-600">. ¡Feliz cumpleaños!</span>}.
        </h1>
        <motion.div className="flex flex-col md:flex-row md:items-center gap-6" {...subir(0.22)}>
          <div className="flex items-center gap-2 text-gray-500 text-lg font-medium">
            <span className="capitalize">{formattedDate}</span>
            {(user?.role === 'admin' || user?.role === 'ti' || user?.role === 'gh') && (
              <button
                onClick={() => setIsPostModalOpen(true)}
                className="w-7 h-7 rounded-full bg-sura-blue text-white flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all shadow-sm"
                title="Nueva Publicación"
              >
                <Plus size={15} strokeWidth={2.5} />
              </button>
            )}
          </div>
          <div className="relative group">
            <div className="relative px-6 py-2.5 bg-blue-50 rounded-2xl border border-blue-100 text-sura-blue font-semibold text-base flex items-center gap-2 shadow-sm">
              <Sparkles size={18} className="text-yellow-500" />
              {randomMessage}
            </div>
          </div>
        </motion.div>
      </header>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Left Column: Feed unificado (Width: 8). Los avisos que aplican se
            abren en su lugar. */}
        <div className="lg:col-span-8 flex flex-col">
          {/* Aviso de feriado bancario — fijado arriba; informativo, se autolimpia por fecha. */}
          <Aparecer noAntesDe={abiertoEn + 380}>
            {feriadoCard && <FeriadoBancarioCard data={feriadoCard} />}
          </Aparecer>
          {/* Banner de venta especial de empleados: solo cuando hay una venta activa */}
          <Aparecer noAntesDe={abiertoEn + 420}>
            {ventaBanner && <VentaEspecialBanner data={ventaBanner} />}
          </Aparecer>
          {/* Premios ANSA 2026: votar por los productos SURA (sale por fechas) */}
          <Aparecer noAntesDe={abiertoEn + 440}><PremiosAnsaBanner /></Aparecer>
          {/* Aviso del estacionamiento de la Torre CA1 (sale por fechas) */}
          <Aparecer noAntesDe={abiertoEn + 460}><EstacionamientoCa1Banner /></Aparecer>
          {/* El feed hace su propia cascada (ver ItemFeed). */}
          <FeedWidget
            refreshTrigger={feedRefreshTrigger}
            canCreate={user?.role === 'admin' || user?.role === 'ti' || user?.role === 'gh'}
            anuncios={anuncios}
          />
        </div>

        {/* Right Column: Widgets (Width: 4). Cada uno carga por su cuenta y
            solo se muestra si tiene algo: se abren en orden, de arriba abajo. */}
        <div className="lg:col-span-4 flex flex-col relative">
          {[
            // Mi Día - siempre arriba
            <MiDiaWidget />,
            <AniversarioWidget />,
            // Smart Widgets - contextuales
            <MisTareasWidget />,
            <TicketsPendientesWidget />,
            <EncuestasPendientesWidget />,
            <VentaEspecialWidget />,
            <TaxiRecepcionWidget />,
            <TaxiPendienteWidget />,
            <PermisosPendientesWidget />,
            <VacacionesPendientesWidget />,
            <ConstanciasPendientesWidget />,
            <ViaticosPendientesWidget />,
            <CalendarioCierreWidget />,
            <SalasReservaWidget />,
            <CumpleanosWidget />,
          ].map((widget, i) => (
            <Aparecer key={i} noAntesDe={abiertoEn + 420 + i * 70}>
              {widget}
            </Aparecer>
          ))}
        </div>
      </div>
      <CreatePostModal
        isOpen={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
        onSuccess={() => setFeedRefreshTrigger(prev => prev + 1)}
      />
    </div>
    </MotionConfig>
  );
};

export default HomeView;
